import type { ReactNode } from 'react';
import { Box, FormControl, FormLabel, List, ListItem, ListItemButton, ListItemDecorator, ListSubheader, Option, Select, Sheet, Typography } from '@mui/joy';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export interface WorkspaceGroup {
  label: string;
  items: { label: string; to: string; icon?: ReactNode; matches?: string[] }[];
}

/** A small, grouped local navigation that leaves the page itself in focus. */
export function WorkspaceNavigation({ title, description, groups, children }: {
  title: string; description: string; groups: WorkspaceGroup[]; children: ReactNode;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const visibleGroups = groups.filter(group => group.items.length);
  const items = visibleGroups.flatMap(group => group.items);
  const active = items.filter(item => [item.to, ...(item.matches || [])].some(path => pathname === path || pathname.startsWith(`${path}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '220px minmax(0, 1fr)' }, gap: { xs: 2.5, lg: 4 }, alignItems: 'start' }}>
    <Sheet component="nav" aria-label={`${title} navigation`} variant="outlined" sx={{ borderRadius: 'lg', p: 2, position: { lg: 'sticky' }, top: 24, maxHeight: { lg: 'calc(100dvh - 136px)' }, overflowY: 'auto' }}>
      <Typography level="title-lg" component="h2">{title}</Typography>
      <Typography level="body-xs" sx={{ mt: 0.5, mb: 2, color: 'text.secondary' }}>{description}</Typography>
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <FormControl><FormLabel>{title} section</FormLabel>
          <Select value={active?.to || null} placeholder="Choose a section" onChange={(_, value) => { if (value) navigate(value); }} slotProps={{ listbox: { sx: { maxHeight: 360, zIndex: 1400 } } }}>
            {visibleGroups.flatMap(group => [
              <ListSubheader key={`heading-${group.label}`}>{group.label}</ListSubheader>,
              ...group.items.map(item => <Option key={item.to} value={item.to}>{item.label}</Option>),
            ])}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        {visibleGroups.map(group => <List key={group.label} size="sm" sx={{ '--ListItem-radius': '8px', '--List-gap': '3px', py: 0, mb: 1 }}>
          <ListSubheader sx={{ px: 1, fontSize: '10px', letterSpacing: '.08em', color: 'text.tertiary' }}>{group.label}</ListSubheader>
          {group.items.map(item => <ListItem key={item.to}><ListItemButton component={Link} to={item.to} selected={active?.to === item.to} aria-current={active?.to === item.to ? 'page' : undefined} sx={{ py: .75, alignItems: 'flex-start', fontWeight: active?.to === item.to ? 700 : 500, '&.Mui-selected': { bgcolor: 'primary.softBg', color: 'primary.softColor' } }}>
            {item.icon && <ListItemDecorator sx={{ minInlineSize: 26, mt: '2px' }}>{item.icon}</ListItemDecorator>}{item.label}
          </ListItemButton></ListItem>)}
        </List>)}
      </Box>
    </Sheet>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>;
}
